import { useCallback, useEffect, useState } from "react";
import { YStack, XStack, Text } from "tamagui";
import { useNavigate } from "react-router-dom";
import liff from "@line/liff";
import toast from "react-hot-toast";
import { colors } from "@styles/colors";
import { useOrder } from "@utils/store/cart/useOrder";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { handleAPIListContextMethods, handleAPIListPaymentMethods, OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import { shouldOfferApplePayInLine } from "@utils/applePay";
import { SkinBottomBar, isGasSkinV2, v2 } from "./skin";
import GasPaymentOptions, { cardSelectionLabel, readLastPayCat } from "./components/GasPaymentOptions";
import { track } from "@lib/tracker";

/**
 * 下訂步驟 Step 2：選付款方式（信用卡 / 電子支付 / 現金或月結），按「前往確認訂單」才進結帳確認頁。
 * 只在瓦斯行開啟電子付款時走這一步（配送時間頁依 allowElectronicPay 決定要不要來）；
 * 關閉時跳過，結帳頁維持原本的貨到付款／月結單一顯示。
 */
const GasShopPayment = () => {
  const navigate = useNavigate();
  const skinV2 = isGasSkinV2();
  const { cusJwtToken } = useCusAuth();
  const { isMonthlyCustomer, paymentSelection, setPaymentSelection } = useOrder();

  const [cards, setCards] = useState<OwnedPaymentMethod[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  // 綁卡成功後遞增 → 重新載入卡片清單並選中新卡。
  const [cardReload, setCardReload] = useState(0);

  // Apple Pay 只在 iPhone 提供（不要求在 LINE App 內）；開發環境不受裝置限制。LINE Pay 一律提供。
  const showApplePay = shouldOfferApplePayInLine({
    os: liff.getOS() ?? null,
    paymentMethod: "apple_pay",
    payable: true,
    devOverride: process.env.NODE_ENV === "development",
  });

  useEffect(() => {
    track("funnel_step", { step: "payment" });
  }, []);

  // 載已綁卡；尚未選過時依上次付款類別預選（置中那張）：現金／卡片（帳戶預設卡 → 第一張）。
  // 電子支付要指定品牌，不預選。
  useEffect(() => {
    if (!cusJwtToken) return;
    let alive = true;
    setCardsLoading(true);
    const contextId = useUser.getState().chooseIdentity?.context_id;
    Promise.all([handleAPIListPaymentMethods(cusJwtToken), contextId ? handleAPIListContextMethods(contextId, cusJwtToken) : Promise.resolve(null)])
      .then(([owned, ctxRes]) => {
        if (!alive) return;
        const list = owned.state === "success" ? owned.items.filter((c) => c.method_type === "credit_card") : [];
        setCards(list);
        const current = useOrder.getState().paymentSelection;
        // 上次選的卡已不在清單（被刪／失效）→ 清掉，避免送單被後端 422 擋下。
        if (current?.type === "credit_card" && !list.some((c) => c.payment_method_id === current.paymentMethodId)) {
          setPaymentSelection(null);
          return;
        }
        if (current) return;
        const lastCat = readLastPayCat();
        if (lastCat === "cash") {
          setPaymentSelection(isMonthlyCustomer ? { type: "monthly" } : { type: "cash_on_delivery" });
        } else if (lastCat === "card" && list.length > 0) {
          const defaultId = ctxRes && ctxRes.state === "success" ? ctxRes.defaultId : null;
          const preferred = (defaultId && list.find((c) => c.payment_method_id === defaultId)) || list[0];
          setPaymentSelection({ type: "credit_card", paymentMethodId: preferred.payment_method_id, label: cardSelectionLabel(preferred) });
        }
      })
      .finally(() => {
        if (alive) setCardsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [cusJwtToken, cardReload, isMonthlyCustomer, setPaymentSelection]);

  const onBound = useCallback(
    (created?: { payment_method_id?: string; card?: { lastfour?: string } }) => {
      if (created?.payment_method_id) {
        setPaymentSelection({
          type: "credit_card",
          paymentMethodId: created.payment_method_id,
          label: `信用卡 •••• ${created.card?.lastfour ?? "----"}`,
        });
      }
      setCardReload((k) => k + 1);
    },
    [setPaymentSelection],
  );

  const isValid = !!paymentSelection;

  const handleNext = () => {
    if (!isValid) {
      toast.error("請選擇付款方式", { id: "payment-required" });
      return;
    }
    navigate("/gasShop/cart");
  };

  // 整頁剛好一個螢幕高、不捲動（PageWrapper 對這條路徑關掉 ScrollView）：
  // 問句與底部按鈕列固定，卡帶舞台吃掉中間全部高度。
  return (
    <YStack gap={10} flex={1} minHeight={0}>
      <YStack gap={2} px={2} flexShrink={0}>
        <Text fontSize={22} fontWeight="800" letterSpacing={-0.6} color={skinV2 ? v2.title : colors.text}>
          預計會怎麼付款？
        </Text>
        <Text fontSize={13} color={skinV2 ? v2.sub : colors.gray}>
          送達時才扣款，並隨時可修改
        </Text>
      </YStack>

      <GasPaymentOptions
        isMonthly={isMonthlyCustomer}
        selected={paymentSelection}
        onSelect={setPaymentSelection}
        onBound={onBound}
        cusJwtToken={cusJwtToken || ""}
        cards={cards}
        loading={cardsLoading}
        showApplePay={showApplePay}
      />

      {skinV2 ? (
        <YStack flexShrink={0}>
          {/* nested：這頁不捲動，不需要 sticky 與底部淡出遮罩 */}
          <SkinBottomBar
            nested
            secondaryLabel="上一步"
            onSecondary={() => navigate("/gasShop/deliveryTime")}
            primaryLabel="前往確認訂單"
            onPrimary={handleNext}
            primaryDisabled={!isValid}
          />
        </YStack>
      ) : (
        <YStack backgroundColor={colors.bg} pt={8} pb={8} flexShrink={0} width="100%">
          <XStack gap={10}>
            <XStack
              flex={1}
              borderRadius={12}
              p={15}
              alignItems="center"
              justifyContent="center"
              borderWidth={1}
              borderColor={colors.borderLight}
              backgroundColor={colors.light}
              onPress={() => navigate("/gasShop/deliveryTime")}
              cursor="pointer"
            >
              <Text color={colors.text} fontSize={16} fontWeight="bold">
                上一步
              </Text>
            </XStack>
            <XStack
              flex={2}
              className={isValid ? "gasshop-btn-gradient" : ""}
              backgroundColor={isValid ? undefined : colors.gray100}
              borderRadius={12}
              p={15}
              alignItems="center"
              justifyContent="center"
              onPress={handleNext}
              opacity={isValid ? 1 : 0.6}
              cursor={isValid ? "pointer" : "default"}
            >
              <Text color={isValid ? "white" : colors.gray} fontSize={16} fontWeight="bold">
                前往確認訂單
              </Text>
            </XStack>
          </XStack>
        </YStack>
      )}
    </YStack>
  );
};

export default GasShopPayment;
